import { DOCUMENT } from '@angular/common'
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Injector,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faImages } from '@fortawesome/free-solid-svg-icons'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { GalleryImage } from 'src/app/shared/interfaces/image-gallery/gallery-image.interface'
import { EmptyComponent } from '../../empty/empty.component'
import { ButtonComponent } from '../../form-elements/button/button.component'

// Opened through ImageGalleryService, which sets the signals below
@Component({
    selector: 'sf-image-gallery-modal',
    imports: [ButtonComponent, EmptyComponent, FontAwesomeModule],
    templateUrl: './image-gallery-modal.component.html',
    styleUrl: './image-gallery-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown)': 'onKeydown($event)',
    },
})
export class ImageGalleryModalComponent {
    activeModal = inject(NgbActiveModal)
    #injector = inject(Injector)
    #host = inject<ElementRef<HTMLElement>>(ElementRef)
    #document = inject(DOCUMENT)

    readonly title = signal('Images')
    readonly images = signal<GalleryImage[]>([])
    readonly selectedImageId = signal<number | null>(null)

    // The display order is decided here so favourite/recency sorting can be added in one place
    readonly orderedImages = computed(() => this.images())

    readonly selectedIndex = computed(() => this.orderedImages().findIndex((x) => x.id === this.selectedImageId()))
    readonly selectedImage = computed(() => this.orderedImages()[this.selectedIndex()] ?? null)
    readonly positionLabel = computed(() => `${this.selectedIndex() + 1} of ${this.orderedImages().length}`)
    readonly stageLabel = computed(() => {
        const title = this.selectedImage()?.title
        return title ? `${title}, image ${this.positionLabel()}` : `Image ${this.positionLabel()}`
    })

    readonly failedImageIds = signal<ReadonlySet<number>>(new Set())

    readonly emptyIcon = faImages

    thumbnails = viewChildren<ElementRef<HTMLButtonElement>>('thumbnail')
    stage = viewChild<ElementRef<HTMLElement>>('stage')

    view(image: GalleryImage): void {
        this.selectedImageId.set(image.id)

        // The clicked thumbnail is removed from the DOM, so move focus into the large view
        afterNextRender(() => this.stage()?.nativeElement.focus(), { injector: this.#injector })
    }

    showThumbnails(): void {
        const imageId = this.selectedImageId()
        this.selectedImageId.set(null)

        // Return focus to the thumbnail that was being viewed
        afterNextRender(
            () => {
                this.thumbnails()
                    .find((x) => x.nativeElement.dataset['imageId'] === String(imageId))
                    ?.nativeElement.focus()
            },
            { injector: this.#injector }
        )
    }

    showPrevious(): void {
        this.#step(-1)
    }

    showNext(): void {
        this.#step(1)
    }

    onImageError(image: GalleryImage): void {
        this.failedImageIds.update((ids) => new Set(ids).add(image.id))
    }

    // Escape steps back from a large image to the thumbnails before closing (the modal is opened with keyboard: false)
    onKeydown(event: KeyboardEvent): void {
        if (event.defaultPrevented || !this.#isTopmostModal()) {
            return
        }

        switch (event.key) {
            case 'Escape':
                event.preventDefault()
                if (this.selectedImage()) {
                    this.showThumbnails()
                } else {
                    this.activeModal.dismiss()
                }
                break
            case 'ArrowLeft':
                if (this.selectedImage()) {
                    event.preventDefault()
                    this.showPrevious()
                }
                break
            case 'ArrowRight':
                if (this.selectedImage()) {
                    event.preventDefault()
                    this.showNext()
                }
                break
        }
    }

    #step(offset: number): void {
        const images = this.orderedImages()
        const index = this.selectedIndex()

        if (index === -1 || images.length < 2) {
            return
        }

        this.selectedImageId.set(images[(index + offset + images.length) % images.length].id)
    }

    // Keys pressed in a modal opened on top of the gallery bubble to the document too, so leave those alone
    #isTopmostModal(): boolean {
        const windows = this.#document.getElementsByTagName('ngb-modal-window')
        return windows[windows.length - 1] === this.#host.nativeElement.closest('ngb-modal-window')
    }
}
